import { AfterViewInit, ChangeDetectionStrategy, Component, inject, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { map, Observable, startWith } from 'rxjs';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import { IDeactivate } from 'src/app/common/guards/save-data-guard/save-data.interface';
import { OperationInfoService } from 'src/app/common/long-operations/operation-info.service';
import ProjectLinks from 'src/app/common/project.links';
import { FormConstructorContent } from 'src/app/interfaces/form-and-field.interface';
import { CreateItemInfo } from 'src/app/interfaces/views.interface';
import { SocialInitiativeCreation } from '../../shared/social-initiative/social-initiative-creation.abstract';

import { SessionService } from 'src/app/auth/session.service';
import { MaybeUndefined } from 'src/app/utils';
import { CommunityFormState, FieldState } from '../community-form-state';

import { CommunityFocus, CommunityStatus } from 'src/app/swagger-api';
import { CommunityMapper, CommunityView } from '../communities-store';
import { CommunityFormData } from '../create-community.interfaces';
import { ManageCommunityService } from '../manage-community.service';
import { UserTypes } from '../../profile/profile.enum';

@Component({
  selector: 'app-create-community',
  templateUrl: './create-community.component.html',
  styleUrls: ['./create-community.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCommunityComponent
  extends SocialInitiativeCreation<CommunityFormData>
  implements AfterViewInit, OnDestroy, IDeactivate
{
  public redirectUrl: string = ProjectLinks.LINK_COMMUNITY_ALL;

  private route = inject(ActivatedRoute);
  private sessionService = inject(SessionService);
  private manageCommunityService = inject(ManageCommunityService);
  private operationInfoService = inject(OperationInfoService);

  private formState: CommunityFormState = new CommunityFormState(
    this.formValueChanges$,
    this.manageCommunityService.onFormAttrChanges,
    this.sessionService.sessionChanges.pipe(
      map(({ userType }) => userType),
      startWith(this.sessionService.userType),
    ),
  );

  public readonly formContent$: Observable<FormConstructorContent> = this.formState.formContent$;
  public readonly focusState$: Observable<FieldState<CommunityFocus>> = this.formState.focusState$;

  public constructor() {
    super();
    this.fetchAttributeMetadata();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.formState.destroy();
  }

  public ngAfterViewInit(): void {
    this.subscribeToControlValueChanges();
    const formData = this.componentForm.childForm.form.getRawValue() as CommunityFormData;
    if (formData.focus === 'CHARITY' && this.router.url.includes(ProjectLinks.LINK_COMMUNITY_CREATE)) {
      this.onCommunityFocusChanged('CHARITY');
    }
  }

  public onCommunityFocusChanged(focus: CommunityFocus): void {
    const formData = this.componentForm.childForm.form.getRawValue() as CommunityFormData;

    this.isFormChanged = false;

    promiseWithCatch(
      this.router.navigate(
        [focus === 'CHARITY' ? ProjectLinks.LINK_CHARITY_CREATE : ProjectLinks.LINK_COMMUNITY_CREATE],
        {
          state: { data: { ...formData, focus } },
        },
      ),
    );
  }

  public onCancel(): void {
    promiseWithCatch(this.router.navigateByUrl(this.redirectUrl));
  }

  protected fetchAttributeMetadata(): void {
    this.subscriptions$.push(
      this.route.params.subscribe(() => {
        const state: MaybeUndefined<CreateItemInfo<CommunityView>> = this.router.getCurrentNavigation().extras
          ?.state as CreateItemInfo<CommunityView>;

        if (state) {
          this.manageCommunityService.fetchAttributeMetadata(state?.data);
          this.redirectUrl = state.returnUrl || this.redirectUrl;
          return;
        }
        const defaultFocus: CommunityFocus =
          this.sessionService.userType === UserTypes.ORGANIZATION ||
          this.router.url.includes(ProjectLinks.LINK_CHARITY_CREATE)
            ? 'CHARITY'
            : 'HEALTH';
        this.manageCommunityService.fetchAttributeMetadata({ focus: defaultFocus, status: CommunityStatus.Open });
      }),
    );
  }

  protected changeEntity(formData: CommunityFormData): void {
    const createRequest = CommunityMapper.isCommunityEntity(formData)
      ? CommunityMapper.toCommunityCreateRequest(formData)
      : CommunityMapper.toCharityCreateRequest(formData);

    this.operationInfoService.setupOperation(
      {
        spinner: 'global',
        button: { id: 'btn-create-community' },
        successfulMessage: CommunityMapper.isCommunityEntity(formData)
          ? 'NEW_COMMUNITY_CREATED_DESCRIPTION'
          : 'NEW_CHARITY_CREATED_DESCRIPTION',
      },
      () => {
        this.manageCommunityService.createEntity(createRequest);
      },
    );
  }
}
